/**
 * 광고 관리 관련 Write 작업 목차
 * 
 * - createBanner() - 배너 생성
 * - updateBanner() - 배너 수정
 * - deleteBanner() - 배너 삭제
 * - toggleCustomBannerStatus() - 커스텀 배너 활성화 상태 토글
 * - toggleLiveBannerStatus() - 라이브 배너 활성화 상태 토글
 * - changeBannerPriority() - 배너 우선순위 변경
 * - uploadBannerImage() - 배너 이미지 업로드
 * - incrementBannerClickCount() - 배너 클릭 수 증가
 * - deleteBannerImage() - 배너 이미지 삭제
 */

import apiClient from "@/utils/api";

/**
 * 배너 생성
 * @param {Object} bannerData - 배너 데이터
 * @returns {Promise<string>} 생성된 배너 ID
 */
export async function createBanner(bannerData) {
  try {
    const response = await apiClient.post("/api/ad/banner", bannerData);
    return response.data.id;
  } catch (error) {
    console.error("배너 생성 오류:", error);
    throw error;
  }
}

/**
 * 배너 수정
 * @param {string} bannerId - 배너 ID
 * @param {Object} updateData - 수정할 데이터
 * @returns {Promise<void>}
 */
export async function updateBanner(bannerId, updateData) {
  try {
    await apiClient.put(`/api/ad/banner/${bannerId}`, updateData);
  } catch (error) {
    console.error("배너 수정 오류:", error);
    throw error;
  }
}

/**
 * 배너 삭제
 * @param {string} bannerId - 배너 ID
 * @returns {Promise<void>}
 */
export async function deleteBanner(bannerId) {
  try {
    await apiClient.delete(`/api/ad/banner/${bannerId}`);
  } catch (error) {
    console.error("배너 삭제 오류:", error);
    throw error;
  }
}

/**
 * 배너 활성화 상태 토글 (커스텀 배너)
 * @param {string} bannerId - 배너 ID
 * @param {boolean} currentStatus - 현재 활성화 상태
 * @returns {Promise<boolean>} 새로운 활성화 상태
 */
export async function toggleCustomBannerStatus(bannerId, currentStatus) {
  try {
    const newStatus = !currentStatus;
    const response = await apiClient.patch(`/api/ad/banner/${bannerId}/status`, {
      active: newStatus,
    });
    return response.data.data.active;
  } catch (error) {
    console.error("배너 상태 변경 오류:", error);
    throw error;
  }
}

/**
 * 라이브 배너 활성화 상태 토글
 * @param {string} liveId - 라이브 ID
 * @param {boolean} currentStatus - 현재 배너 상태
 * @returns {Promise<boolean>} 새로운 배너 상태
 */
export async function toggleLiveBannerStatus(liveId, currentStatus) {
  try {
    const newStatus = !currentStatus;
    const response = await apiClient.patch(`/api/ad/live/${liveId}/banner`, {
      bannerOn: newStatus,
    });
    return response.data.data.bannerOn;
  } catch (error) {
    console.error("라이브 배너 상태 변경 오류:", error);
    throw error;
  }
}

/**
 * 배너 우선순위 변경
 * @param {string} source - 출처 ('live' 또는 'custom')
 * @param {string} id - ID (라이브 ID 또는 배너 ID)
 * @param {number} change - 우선순위 변화량 (양수: 증가, 음수: 감소)
 * @returns {Promise<number>} 새로운 우선순위
 */
export async function changeBannerPriority(source, id, change) {
  try {
    const newPriority = Math.max(0, change);
    const response = await apiClient.patch(`/api/ad/${source}/${id}/priority`, {
      priority: newPriority,
    });
    return response.data.data.priority;
  } catch (error) {
    console.error("우선순위 변경 오류:", error);
    throw error;
  }
}

/**
 * 배너 이미지 업로드 (문서 생성 없이)
 * 신규 배너 추가 시 문서가 생성되기 전에 이미지를 업로드할 때 사용
 * @param {File} imageFile - 이미지 파일
 * @param {Function} onProgress - 업로드 진행률 콜백 (선택)
 * @returns {Promise<{downloadURL: string, fileName: string}>}
 */
export async function uploadBannerImageWithoutId(imageFile, onProgress = null) {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await apiClient.post("/api/ad/banner/image", formData, config);
    return {
      downloadURL: response.data.data.url,
      fileName: response.data.data.fileName,
    };
  } catch (error) {
    console.error("배너 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 배너 이미지 업로드 (기존 문서에 연결)
 * @param {File} imageFile - 이미지 파일
 * @param {string} bannerId - 배너 ID
 * @param {Function} onProgress - 업로드 진행률 콜백 (선택)
 * @returns {Promise<{downloadURL: string, fileName: string}>}
 */
export async function uploadBannerImage(imageFile, bannerId, onProgress = null) {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await apiClient.post(`/api/ad/banner/${bannerId}/image`, formData, config);
    return {
      downloadURL: response.data.data.url,
      fileName: response.data.data.fileName,
    };
  } catch (error) {
    console.error("배너 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 배너 클릭 수 증가
 * @param {string} source - 출처 ('live' 또는 'custom')
 * @param {string} id - ID (라이브 ID 또는 배너 ID)
 * @returns {Promise<void>}
 */
export async function incrementBannerClickCount(source, id) {
  try {
    await apiClient.post(`/api/ad/${source}/${id}/click`);
  } catch (error) {
    console.error("클릭 횟수 증가 오류:", error);
    // 클릭 수 증가 실패는 사용자 경험에 영향을 주지 않도록 에러를 던지지 않음
  }
}

/**
 * 배너 이미지 삭제
 * @param {string} fileName - 파일명
 * @returns {Promise<void>}
 */
export async function deleteBannerImage(fileName) {
  try {
    await apiClient.delete("/api/storage", {
      data: { path: "banner", fileName },
    });
  } catch (error) {
    console.error("배너 이미지 삭제 오류:", error);
    throw error;
  }
}
